import React, { useState } from 'react';
import { CheckCircle2, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QuickStatOverrideDialog } from '@/components/live-scoring/QuickStatOverrideDialog';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';

interface OverUnderScoringOptionProps {
  questionId?: string;
  fixtureId?: string;
  statTypeCode?: string | null;
  option: {
    option_id?: string;
    option_text_en: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    third_image_en?: string | null;
    third_image_fr?: string | null;
    is_correct: boolean;
    stats?: any;
    grading_data?: {
      threshold?: number;
      comparison?: 'over' | 'under';
      playerId?: string | number;
      teamId?: string | number;
    };
  };
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  onOverrideSuccess?: () => void;
}

export function OverUnderScoringOption({
  questionId,
  fixtureId: fixtureIdProp,
  statTypeCode,
  option,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onSave,
  onCancel,
  onOverrideSuccess,
}: OverUnderScoringOptionProps) {
  const optionText =
    language === 'en' ? option.option_text_en : option.option_text_fr || option.option_text_en;
  const optionImage =
    language === 'en' ? option.option_image_en : option.option_image_fr || option.option_image_en;
  const thirdImage =
    language === 'en' ? option.third_image_en : option.third_image_fr || option.third_image_en;

  const isCorrect = option.is_correct;
  const threshold = option.grading_data?.threshold;
  const comparison = option.grading_data?.comparison;
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  // Extract playerId or teamId from grading_data
  const playerId = option.grading_data?.playerId?.toString();
  const teamId = option.grading_data?.teamId?.toString();

  // Get fixtureId from matchStats or prop (prop takes precedence)
  const fixtureId = fixtureIdProp || matchStats?.match.id;

  // Extract stat value for display
  let statValue: number | null = null;
  try {
    if (statTypeCode && option.stats) {
      if (typeof option.stats === 'object' && !Array.isArray(option.stats)) {
        const value = option.stats[statTypeCode];
        statValue = typeof value === 'number' ? value : null;
      }
    }
  } catch (error) {
    console.error('Error extracting stat value:', error);
  }

  const displayValue = statValue !== null ? statValue.toString() : '0';

  const handleEditClick = () => {
    if (manualScoring) {
      // If we have questionId, fixtureId, (playerId or teamId), and statTypeCode, use QuickStatOverrideDialog
      if (questionId && fixtureId && (playerId || teamId) && statTypeCode) {
        setIsOverrideDialogOpen(true);
      } else {
        // Fallback to inline editing if we don't have all required data
        setEditingValue(displayValue);
        setIsEditing(true);
      }
    }
  };

  const handleSave = () => {
    if (option.option_id && onStatChange) {
      const numValue = parseFloat(editingValue);
      if (!isNaN(numValue)) {
        onStatChange(option.option_id, numValue);
      }
    }
    if (option.option_id && onSave) {
      onSave(option.option_id);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (option.option_id && onCancel) {
      onCancel(option.option_id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      {/* Option Card */}
      <div
        className={`relative w-full p-6 rounded-lg border transition-colors flex flex-col items-center justify-center min-h-[140px] ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark Icon - Top Right */}
        {isCorrect && (
          <div className="absolute top-2 right-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
        )}

        {/* Option Image (if available) */}
        {(optionImage || thirdImage) && (
          <div className="mb-3">
            {optionImage && (
              <img src={optionImage} alt={optionText} className="h-16 w-16 object-contain" />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-16 w-16 object-contain" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="text-center">
          <span className={`text-sm font-medium ${isCorrect ? 'text-gray-900' : 'text-gray-700'}`}>
            {optionText}
          </span>
          {threshold !== undefined && threshold !== null && (
            <div className="text-xs text-gray-500 mt-1">
              {comparison === 'over' ? 'Over' : 'Under'} {threshold}
            </div>
          )}
        </div>
      </div>

      {/* Stat Value with Edit Controls - Center aligned */}
      {statTypeCode && (
        <div className="flex items-center justify-center gap-2 w-full">
          {isEditing ? (
            <>
              <Input
                type="number"
                step="0.1"
                value={editingValue}
                onChange={e => setEditingValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-8 w-16 text-center"
                autoFocus
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleSave}
                className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="h-7 w-7 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <span
                className={`text-lg font-semibold ${isCorrect ? 'text-green-600' : 'text-gray-600'}`}
              >
                {displayValue}
              </span>
              {manualScoring && (
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Edit stat value"
                >
                  <Pencil className="h-4 w-4 text-orange-600" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Quick Stat Override Dialog */}
      {questionId && fixtureId && (playerId || teamId) && statTypeCode && (
        <QuickStatOverrideDialog
          open={isOverrideDialogOpen}
          onOpenChange={setIsOverrideDialogOpen}
          questionId={questionId}
          fixtureId={fixtureId}
          playerId={playerId}
          teamId={teamId}
          statType={statTypeCode}
          currentValue={statValue ?? undefined}
          onSuccess={() => {
            setIsOverrideDialogOpen(false);
            onOverrideSuccess?.();
          }}
        />
      )}
    </div>
  );
}
